import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PickText } from 'helpers/pickLabel';
import styles from './PickSummary.module.css';

/**
 * One pick, said in full: the market on one line, the team and the pick on
 * the next, and an "i" that opens the whole story - match, market, team, pick,
 * odds and what the market means - for descriptions too long to fit the row.
 * Used on the slip and on open and settled tickets, so they read the same.
 */
export const PickSummary = ({
  info,
  odds,
  match,
  pickClassName,
}: {
  info: PickText;
  /** The host's colour for the picked outcome (slip: its accent). */
  pickClassName?: string;
  odds?: number | null;
  /** "Liverpool - Villarreal", shown in the details. */
  match?: string;
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <span className={styles.summary}>
      <span className={styles.market}>{info.market}</span>
      <span className={styles.pickLine}>
        <span className={`${styles.pick} ${pickClassName ?? ''}`}>
          {info.team && <b className={styles.team}>{info.team}</b>}
          {info.team && <span className={styles.dot}> · </span>}
          {info.pick}
        </span>
        <button
          type="button"
          className={styles.infoButton}
          data-open={open}
          aria-expanded={open}
          aria-label={t('pick_info_toggle')}
          title={t('pick_info_toggle')}
          onClick={(e) => {
            e.stopPropagation();
            setOpen((v) => !v);
          }}
        >
          i
        </button>
      </span>
      {open && (
        <span className={styles.details} onClick={(e) => e.stopPropagation()}>
          {match && (
            <span className={styles.row}>
              <small>{t('pick_info_match')}</small>
              <span>{match}</span>
            </span>
          )}
          <span className={styles.row}>
            <small>{t('pick_info_market')}</small>
            <span>{info.market}</span>
          </span>
          {info.team && (
            <span className={styles.row}>
              <small>{t('pick_info_team')}</small>
              <span>{info.team}</span>
            </span>
          )}
          <span className={styles.row}>
            <small>{t('pick_info_pick')}</small>
            <span>{info.pick}</span>
          </span>
          {odds != null && (
            <span className={styles.row}>
              <small>{t('pick_info_odds')}</small>
              <span>{Number(odds).toFixed(2)}</span>
            </span>
          )}
          <span className={styles.explain}>
            {info.description ?? t('pick_info_no_description')}
          </span>
        </span>
      )}
    </span>
  );
};
